import classnames from "classnames";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { commafy } from "src/utils";

type Props = {
  min: number;
  max: number;
  onChange: React.ChangeEventHandler<HTMLInputElement> | any;
};

const MultiRangeSlider: FC<Props> = ({ min, max, onChange }) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const minValRef: any = useRef(null);
  const maxValRef: any = useRef(null);
  const range: any = useRef(null);
  const right: any = useRef(null);
  const left: any = useRef(null);

  // Convert to percentage
  const getPercent = useCallback(
    (value) => Math.round(((value - min) / (max - min)) * 100),
    [min, max]
  );

  // Set width of the range to decrease from the right side
  useEffect(() => {
    if (maxValRef.current) {
      const minPercent = getPercent(minVal);
      const maxPercent = getPercent(+maxValRef.current.value); // Preceding with '+' converts the value from type string to type number

      if (range.current) {
        right.current.style.right = `${
          minPercent < 50 ? minPercent - 5 : minPercent - 20
        }%`;
        range.current.style.right = `${minPercent}%`;
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [minVal, getPercent]);

  // Set width of the range to decrease from the right side
  useEffect(() => {
    const maxPercent = getPercent(maxVal);
    left.current.style.right = `${
      maxPercent < 50 ? maxPercent : maxPercent - 28
    }%`;
    if (minValRef.current) {
      const minPercent = getPercent(+minValRef.current.value);
      const maxPercent = getPercent(maxVal);

      if (range.current) {
        range.current.style.width = `${maxPercent - minPercent}%`;
      }
    }
  }, [maxVal, getPercent]);

  // Get min and max values when their state changes
  useEffect(() => {
    onChange({ min: minVal, max: maxVal });
  }, [minVal, maxVal, onChange]);

  return (
    <div
      className="container relative mt-7"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="range"
        min={min}
        step={100}
        max={max}
        value={minVal}
        ref={minValRef}
        onChange={(event) => {
          const value = Math.min(+event.target.value, maxVal - 1);
          setMinVal(value);
          event.target.value = value.toString();
        }}
        className={classnames("thumb thumb--zindex-3 w-full md:w-[200px]", {
          "thumb--zindex-5": minVal < max - 100,
        })}
      />
      <input
        type="range"
        min={min}
        step={100}
        max={max}
        name="foo"
        value={maxVal}
        ref={maxValRef}
        onChange={(event) => {
          const value = Math.max(+event.target.value, minVal + 1);
          setMaxVal(value);
          event.target.value = value.toString();
        }}
        className="thumb thumb--zindex-4 w-full md:w-[200px]"
      />
      <div
        className="absolute -top-7 w-auto bg-blue-550 px-2 text-center text-xs text-white"
        ref={right}
      >
        {commafy(minVal)}
      </div>
      <div
        className="absolute -top-7 w-auto bg-blue-550 px-2 text-center text-xs text-white"
        ref={left}
      >
        {commafy(maxVal)}
      </div>
      <div className="slider w-full md:w-[200px]">
        <div className="slider__track" />
        <div ref={range} className="slider__range" />
        <div className="slider__left-value">{commafy(minVal)}</div>
        <div className="slider__right-value">{commafy(maxVal)}</div>
      </div>
    </div>
  );
};

export default MultiRangeSlider;
